<template>
  <div class="atividade-popup-52000">
    <div class="atividade-popup-52000__shell">
      <div class="atividade-popup-52000__base"></div>
      <img :src="getImageUrlWithCDN_LOCAL('/home2/atividade-popup-crad-trumpet.webp')" class="atividade-popup-52000__trumpet" alt="" />
      <div class="atividade-popup-52000__card" :style="{ backgroundImage: `url(${cardBgUrl})` }">
        <div class="atividade-popup-52000__card-inner">
          <div class="atividade-popup-52000__header">
            <span class="atividade-popup-52000__title">Aviso de evento</span>
          </div>

          <div
            ref="tabsRef"
            class="atividade-popup-52000__tabs"
            :style="tabsStyle"
            :class="{
              'atividade-popup-52000__tabs--single': tabsLayout === 'single',
              'atividade-popup-52000__tabs--measure': tabsLayout === 'measure',
              'atividade-popup-52000__tabs--fill': tabsLayout === 'fill',
              'atividade-popup-52000__tabs--overflow': tabsLayout === 'overflow',
            }"
          >
            <div
              v-for="(e, index) in list"
              :key="e.id"
              :class="['atividade-popup-52000__tab', { 'atividade-popup-52000__tab--active': activeIndex === index }]"
              @click="selectTab(index)"
            >
              <span class="atividade-popup-52000__tab-text" v-html="e.title"></span>
            </div>
          </div>

          <div class="atividade-popup-52000__body">
            <div v-if="activeIndex > 0" class="atividade-popup-52000__arrow atividade-popup-52000__arrow--left" @click="prevMessage">
              <img :src="getImageUrlWithCDN_LOCAL('/public/arrow_right_list_img.png')" class="atividade-popup-52000__arrow-icon" alt="" />
            </div>

            <div ref="contentRef" class="atividade-popup-52000__content">
              <img
                v-if="activeForm.img"
                :src="activeForm.img"
                class="atividade-popup-52000__content-img"
                @click="jumpUrl(activeForm.jump_url)"
              />
              <div v-else class="atividade-popup-52000__content-html" v-html="activeForm.content"></div>
            </div>

            <div
              v-if="activeIndex < list.length - 1"
              class="atividade-popup-52000__arrow atividade-popup-52000__arrow--right"
              @click="nextMessage"
            >
              <img :src="getImageUrlWithCDN_LOCAL('/public/arrow_right_list_img.png')" class="atividade-popup-52000__arrow-icon" alt="" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useAtividadeModel } from './js/index.js'

const props = defineProps({
  list: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    default: 'atividade',
  },
  isHasUnRead: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['change', 'close'])

const { activeForm, change, jumpUrl } = useAtividadeModel(props, emit)

const cardBgUrl = getImageUrlWithCDN_LOCAL('/home2/atividade-popup-crad-bg.webp')

const tabsRef = ref(null)
const contentRef = ref(null)
const tabsLayout = ref('measure')
const tabSlotWidth = ref(0)

const tabsStyle = computed(() => ({
  '--tab-slot-width': `${tabSlotWidth.value}px`,
}))

const getTabsGap = (container) => {
  const styles = getComputedStyle(container)
  const gapValue = styles.columnGap || styles.gap
  const gap = Number.parseFloat(gapValue)
  return Number.isFinite(gap) ? gap : 8
}

const resetTabsScroll = (container = tabsRef.value) => {
  if (container) container.scrollLeft = 0
}

let tabsLayoutToken = 0
let tabsLayoutUpdateRaf = 0

const updateTabsLayout = async () => {
  const token = ++tabsLayoutToken
  const count = props.list.length
  const container = tabsRef.value

  if (!container) {
    tabsLayout.value = count === 1 ? 'single' : 'measure'
    tabSlotWidth.value = 0
    return
  }

  if (count <= 0) {
    tabsLayout.value = 'measure'
    tabSlotWidth.value = 0
    resetTabsScroll(container)
    return
  }

  if (count === 1) {
    tabsLayout.value = 'single'
    tabSlotWidth.value = 0
    resetTabsScroll(container)
    return
  }

  tabsLayout.value = 'measure'
  tabSlotWidth.value = 0

  await nextTick()

  if (token !== tabsLayoutToken) return

  const currentContainer = tabsRef.value
  if (!currentContainer) return

  const gap = getTabsGap(currentContainer)
  const tabs = Array.from(currentContainer.children)
  const naturalTabsWidth = tabs.reduce((total, tab) => total + tab.scrollWidth, 0)
  const naturalTotalWidth = naturalTabsWidth + gap * (count - 1)
  const containerWidth = currentContainer.clientWidth
  const slotWidth = Math.max(0, (containerWidth - gap * (count - 1)) / count)
  const everyTabFitsSlot = tabs.every((tab) => tab.scrollWidth <= slotWidth + 1)

  if (naturalTotalWidth > containerWidth || !everyTabFitsSlot) {
    tabsLayout.value = 'overflow'
    tabSlotWidth.value = 0
    await nextTick()
    scrollTabIntoView(activeIndex.value)
    return
  }

  tabsLayout.value = 'fill'
  tabSlotWidth.value = slotWidth
  resetTabsScroll(currentContainer)
}

const scheduleTabsLayoutUpdate = () => {
  if (typeof requestAnimationFrame !== 'function') {
    updateTabsLayout()
    return
  }

  if (tabsLayoutUpdateRaf) cancelAnimationFrame(tabsLayoutUpdateRaf)

  tabsLayoutUpdateRaf = requestAnimationFrame(() => {
    tabsLayoutUpdateRaf = 0
    updateTabsLayout()
  })
}

let tabsResizeObserver = null

onMounted(() => {
  scheduleTabsLayoutUpdate()

  if (tabsRef.value && typeof ResizeObserver !== 'undefined') {
    tabsResizeObserver = new ResizeObserver(() => {
      scheduleTabsLayoutUpdate()
    })
    tabsResizeObserver.observe(tabsRef.value)
  }

  window.addEventListener('resize', scheduleTabsLayoutUpdate, { passive: true })
})

onUnmounted(() => {
  if (tabsLayoutUpdateRaf && typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(tabsLayoutUpdateRaf)
  }
  tabsLayoutUpdateRaf = 0
  tabsResizeObserver?.disconnect()
  tabsResizeObserver = null
  window.removeEventListener('resize', scheduleTabsLayoutUpdate)
})

watch(() => `${props.list.length}\u0001${props.list.map((item) => item.title).join('\u0001')}`, scheduleTabsLayoutUpdate, { flush: 'post' })

const activeIndex = computed(() => {
  const idx = props.list.findIndex((item) => item.id === activeForm.id)
  return idx >= 0 ? idx : 0
})

const resetContentScroll = () => {
  nextTick(() => {
    if (contentRef.value) {
      contentRef.value.scrollTop = 0
    }
  })
}

const scrollTabIntoView = (index) => {
  if (tabsLayout.value !== 'overflow') return

  nextTick(() => {
    const container = tabsRef.value
    if (!container) return

    const tab = container.children[index]
    if (!tab) return

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth)
    let targetLeft = 0

    if (index === 0) {
      targetLeft = 0
    } else if (index === props.list.length - 1) {
      targetLeft = maxScrollLeft
    } else {
      targetLeft = tab.offsetLeft - (container.clientWidth - tab.offsetWidth) / 2
      targetLeft = Math.min(maxScrollLeft, Math.max(0, targetLeft))
    }

    container.scrollTo({ left: targetLeft, behavior: 'smooth' })
  })
}

const selectTab = (index) => {
  if (index < 0 || index >= props.list.length) return
  change(props.list[index])
}

const prevMessage = () => {
  selectTab(activeIndex.value - 1)
}

const nextMessage = () => {
  selectTab(activeIndex.value + 1)
}

watch(activeIndex, (index) => {
  scrollTabIntoView(index)
  resetContentScroll()
})

defineExpose({
  change,
  jumpUrl,
})
</script>

<style lang="less" scoped>
.atividade-popup-52000 {
  width: 90vw;
  max-width: 360px;

  &__shell {
    position: relative;
    padding-top: 24px;
  }

  &__base {
    position: absolute;
    top: 40px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #d2fdc4;
    border-radius: 20px;
    z-index: 0;
  }

  &__trumpet {
    position: absolute;
    top: 0px;
    right: -10px;
    width: 115px;
    height: auto;
    z-index: 1;
    pointer-events: none;
  }

  &__card {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 343px;
    background-repeat: no-repeat;
    background-position: center top;
    background-size: 100% 100%;
    border-radius: 16px;
    overflow: hidden;
  }

  &__card-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  &__header {
    flex-shrink: 0;
    padding: 17px 12px 10px 20px;
  }

  &__title {
    font-size: 27px;
    font-weight: 900;
    font-style: italic;
    line-height: 1.2;
    background: linear-gradient(180deg, #ffffff 33%, #77ff48 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  &__tabs {
    flex-shrink: 0;
    display: flex;
    gap: 4px;
    width: calc(100% - 40px);
    margin: 4px 20px 0;
    overflow-x: hidden;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    box-sizing: border-box;

    &::-webkit-scrollbar {
      display: none;
    }

    &--single {
      .atividade-popup-52000__tab {
        flex: 1 1 100%;
        width: 100%;
      }
    }

    &--measure,
    &--overflow {
      .atividade-popup-52000__tab {
        flex: 0 0 auto;
        width: max-content;
        max-width: none;
      }
    }

    &--fill {
      .atividade-popup-52000__tab {
        flex: 0 0 var(--tab-slot-width);
        width: var(--tab-slot-width);
        min-width: 0;
      }
    }

    &--overflow {
      overflow-x: auto;
    }
  }

  &__tab {
    min-height: 20px;
    box-sizing: border-box;
    padding: 3px 12px;
    border-radius: 8px 8px 0 0;
    background: rgba(56, 84, 47, 0.44);
    box-shadow: inset 0 0 6px 0 rgba(255, 255, 255, 0.28);
    cursor: pointer;
    transition: background-color 0.2s ease;
    text-align: center;

    &--active {
      background: linear-gradient(180deg, #befdd5 33%, #9fff5b 100%);

      .atividade-popup-52000__tab-text {
        color: #1a5c2e;
        font-weight: 600;
      }
    }
  }

  &__tab-text {
    display: inline-block;
    font-size: 11px;
    line-height: 1.3;
    color: #ffffff;
    text-align: center;
    white-space: nowrap;
  }

  &__body {
    position: relative;
    flex: 1;
    min-height: 0;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  &__content {
    width: 100%;
    height: 256px;
    max-height: 256px;
    padding: 4px 6px;
    background: rgba(255, 255, 255, 0.45);
    border-radius: 20px;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-y;
    box-sizing: border-box;
  }

  &__content-img {
    display: block;
    width: 100%;
    height: auto;
    cursor: pointer;
  }

  &__content-html {
    padding: 12px 10px;
    font-size: 12px;
    line-height: 1.5;
    color: #1a5c2e;
    word-break: break-word;

    :deep(a) {
      color: #2563eb;
      text-decoration: underline;
    }

    :deep(img) {
      max-width: 100%;
      height: auto;
    }
  }

  &__arrow {
    position: absolute;
    top: 50%;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    transform: translateY(-50%);
    cursor: pointer;

    &--left {
      left: 6px;

      .atividade-popup-52000__arrow-icon {
        transform: rotate(180deg);
      }
    }

    &--right {
      right: 6px;
    }
  }

  &__arrow-icon {
    width: 28px;
    height: 28px;
    opacity: 1;
  }
}
</style>
